<template>
	<div>
		<ValidationObserver
			ref="registerConfirm"
			v-slot="{ invalid, errors }"
			class="sign-up"
			tag="form"
			@submit.prevent="onRegisterConfirm"
			novalidate
		>
			<h2 v-html="$t('login.registerConfirmTitle')" v-if="!hideTitle"/>

			<GeneralMessage
				v-if="registerConfirmResponse.message"
				:message="registerConfirmResponse.message"
				:type="registerConfirmResponse.type"
				@close="registerConfirmResponse.message = ''"
			/>

			<ValidationProvider
				class="input-wrapper"
				name="token"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="text"
					:placeholder="$t('login.confirmTokenLabel')"
					:label="$t('login.confirmTokenLabel')"
					v-model="registerConfirm.token"
					:error="invalid && validated"
          :readonly="tokenLocked"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

      <template v-if="createUser">
        <!-- PASSWORD -->
        <ValidationProvider
          class="input-wrapper"
          name="password"
          rules="required|complexpw"
          vid="password"
          v-slot="{ errors, invalid, validated, dirty }"
          tag="div"
          :customMessages="{ required: $t('forms.errors.fieldEmpty'), complexpw: $t('forms.errors.password') }"
        >
          <FormInput
            type="password"
            autocomplete="new-password"
            :placeholder="$t('forms.labels.newPassword')"
            :label="$t('forms.labels.newPassword')"
            v-model="registerConfirm.password"
            :error="invalid && validated"
            :disabled="disabled"
            required
          />
          <span
            class="input-error"
            v-if="invalid && validated"
            v-html="errors[0]"
          />
        </ValidationProvider>

        <!-- PASSWORD CONFIRM -->
        <ValidationProvider
          class="input-wrapper"
          name="confirm"
          vid="confirm"
          rules="required|confirmed:password"
          v-slot="{ errors, invalid, validated, dirty }"
          tag="div"
          :customMessages="{ required: $t('forms.errors.fieldEmpty'), confirmed: $t('forms.errors.passwordMatch') }"
        >
          <FormInput
            type="password"
            autocomplete="new-password"
            :placeholder="$t('forms.labels.newPasswordRepeat')"
            :label="$t('forms.labels.newPasswordRepeat')"
            v-model="registerConfirm.confirm"
            :error="invalid && validated"
            :disabled="disabled"
            required
          />
          <span
            class="input-error"
            v-if="invalid && validated"
            v-html="errors[0]"
          />
        </ValidationProvider>
      </template>

			<Button
				type="submit"
				level="primary"
				:text="$t('login.registerConfirmButton')"
				:disabled="disabled"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import GenericMixin from '@/mixins/GenericVueMixin';
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import { getUrlParams } from '@/utils/urlParams';

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputCheckbox,
		Button,
		GeneralMessage,
	},
	props: {
		hideTitle: Boolean,
		phone: {
			type: Boolean,
			default: false,
		},
		confirm: {
			type: String,
			default: '',
		}
	},
	data() {
		return {
			registerConfirm: {
				token: '',
				password: '',
				confirm: '',
			},
      tokenLocked: false,
      createUser: false,
			confirmUrl: this.$root.options.confirmUrl || this.confirm,
			registerConfirmResponse: {
				type: 'info',
				message: '',
			},
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn"
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		disabled() {
			return !!this.isLoggedIn || this.loading;
		},
	},
	methods: {
		async onRegisterConfirm() {
			const isValid = await this.$refs.registerConfirm.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					const response = await this.$store.dispatch('account/registerConfirm', this.registerConfirm)
					if (response.redirect && response.redirect.key) {
						return this.$store.dispatch('redirect', {
							name: response.redirect.key,
							params: response.redirect.params || {},
						})
					}
					this.resetForm();
					// this.registerConfirmResponse.message = this.$t('login.successRegister');
					// this.registerConfirmResponse.type = 'success';
					this.$emit('confirmed');
					return this.$store.dispatch('redirect', {
						name: 'custom',
						url: this.confirmUrl
					})
				} catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onRegisterConfirm(), retry: true })
          } else {
            let message = this.$t('forms.errors.generic')
            const data = err.response.data;

            if (err.response.status === 400) {
              message = this.$t('login.errorConfirm')
            }
            if (data) {
              if (data.redirect && data.redirect.key) {
                return this.$store.dispatch('redirect', {
                  name: data.redirect.key,
                  params: data.redirect.params || {},
                })
              }

              if (data.message || data.errors) {
                message = data.errors && Object.values(data.errors).length
                  ? Object.values(data.errors).flat().join()
                  : data.message
              }
            }

            this.registerConfirmResponse.message = message;
            this.registerConfirmResponse.type = 'error';
          }
				}
				this.loading = false;
			}
		},
		resetForm(form, inputs = true) {
			try {
				if (!form || form === 'register') {
					if (inputs) {
						this.registerConfirm.token = '';
					}
					this.registerConfirmResponse.message = '';
					this.$refs.registerConfirm.reset();
				}
				if (!form) {
					this.loading = false;
				}
				if (inputs) {
					document.activeElement.blur();
				}
			}
			catch(err) {}
		},
	},
	mounted() {
		const params = getUrlParams()
		this.registerConfirm.token = params.token
    this.tokenLocked = !!params.token
    this.createUser = params.createUser === 'true'
	}
}
</script>

<style>

</style>
